import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: { fontSize: '23px', fontWeight: 550, color: theme.color.grey.neutral400, paddingLeft: '5px' },
    subtitle: { fontSize: '14px', paddingLeft: '5px', paddingRight: '10px' },
    content: { padding: '12px 24px 16px 24px' },
    field: { marginBottom: '16px' },
  })
);

export interface AchievementOption {
  achievementId: string;
  name: string;
  isEnabled: boolean;
  levels: { achievementLevelId: string; name: string; position: number }[];
}

interface Props {
  open: boolean;
  volunteerName: string;
  subtitle?: string;
  achievements: AchievementOption[];
  heldLevelIds: string[];
  saving: boolean;
  onClose: () => void;
  onAward: (achievementLevelId: string, note: string) => void;
}

export const AwardAchievementDialog = ({
  open,
  volunteerName,
  subtitle,
  achievements,
  heldLevelIds,
  saving,
  onClose,
  onAward,
}: Props) => {
  const classes = useStyles();
  const [achievementId, setAchievementId] = useState('');
  const [levelId, setLevelId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setAchievementId('');
      setLevelId('');
      setNote('');
    }
  }, [open]);

  const options = useMemo(
    () =>
      achievements
        .filter((achievement) => achievement.isEnabled)
        .map((achievement) => ({
          ...achievement,
          levels: [...achievement.levels]
            .sort((a, b) => a.position - b.position)
            .filter((level) => !heldLevelIds.includes(level.achievementLevelId)),
        }))
        .filter((achievement) => achievement.levels.length > 0),
    [achievements, heldLevelIds]
  );
  const levels = options.find((achievement) => achievement.achievementId === achievementId)?.levels ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography className={classes.title}>Award Level</Typography>
          <IconButton aria-label='Close' color='inherit' onClick={onClose}>
            <Close color='action' />
          </IconButton>
        </Box>
        <Box pr={3} mt={1}>
          <Typography className={classes.subtitle} color='textSecondary'>
            {subtitle ?? `Award an achievement level to ${volunteerName}.`}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {options.length === 0 ? (
          <Typography style={{ fontSize: '14px', color: '#808080' }}>
            There are no levels left to award. Enable an achievement or add levels in the admin console.
          </Typography>
        ) : (
          <>
            <FormControl fullWidth variant='outlined' className={classes.field}>
              <InputLabel>Achievement</InputLabel>
              <Select
                value={achievementId}
                onChange={(e) => {
                  setAchievementId(e.target.value as string);
                  setLevelId('');
                }}
                input={<OutlinedInput label='Achievement' />}
              >
                {options.map((achievement) => (
                  <MenuItem key={achievement.achievementId} value={achievement.achievementId}>
                    {achievement.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth variant='outlined' className={classes.field} disabled={!achievementId}>
              <InputLabel>Level</InputLabel>
              <Select
                value={levelId}
                onChange={(e) => setLevelId(e.target.value as string)}
                input={<OutlinedInput label='Level' />}
              >
                {levels.map((level) => (
                  <MenuItem key={level.achievementLevelId} value={level.achievementLevelId}>
                    {level.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              variant='outlined'
              label='Note'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              rows={2}
              helperText='Only visible to admins'
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <TabletButton variant='text' color='error' onClick={onClose} disabled={saving} style={{ marginRight: 8 }}>
          Cancel
        </TabletButton>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={() => onAward(levelId, note)}
          disabled={saving || !levelId}
        >
          {saving ? 'Awarding...' : 'Award'}
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
